import type {
  AssignmentStatus,
  Experiment,
  ExperimentAssignment,
  ExperimentOutcome,
} from "@andrum/schemas";
import { getSupabase } from "@/lib/supabase/client";
import {
  getExperimentById,
  MOCK_EXPERIMENTS,
} from "@/features/experiments/data/mock-experiments";
import { enqueue } from "@/lib/offline/queue";
import {
  getReasonLabel,
  recommendExperiment,
  type ExperimentGlobalStat,
  type RecommendContext,
  type SimilarSignal,
} from "@/features/experiments/lib/recommend";
import { listRecentCheckIns } from "@/features/check-in/api/check-in-api";
import { hasEntitlement } from "@/lib/billing/entitlements";
import { localizeExperiments } from "@/i18n/catalog";
import { getLocale } from "@/i18n";
import { API_BASE, getAccessToken } from "@/lib/api/session";

/** Map app locale code → experiment_content.locale */
function contentLocale(): string {
  const loc = getLocale();
  if (loc === "sv") return "sv-SE";
  return loc; // en, fr, de, ja
}

function mapJoinedExperiment(row: {
  id: string;
  slug: string;
  category: string;
  difficulty: number;
  duration_minutes: number;
  min_duration_minutes: number;
  premium: boolean;
  experiment_content: Array<{
    title: string;
    summary: string;
    rationale: string;
    instructions: unknown;
    smaller_version: string;
    reflection_questions: unknown;
  }> | null;
}): Experiment | null {
  const content = row.experiment_content?.[0];
  if (!content) return null;
  return {
    id: row.id,
    slug: row.slug,
    category: row.category as Experiment["category"],
    difficulty: row.difficulty,
    durationMinutes: row.duration_minutes,
    minDurationMinutes: row.min_duration_minutes,
    premium: row.premium,
    title: content.title,
    summary: content.summary,
    rationale: content.rationale,
    instructions: content.instructions as string[],
    smallerVersion: content.smaller_version,
    reflectionQuestions: content.reflection_questions as string[],
  };
}

function mapAssignment(row: {
  id: string;
  experiment_id: string;
  status: string;
  recommendation_reason_code: string;
  scheduled_for: string | null;
  started_at: string | null;
  completed_at: string | null;
  content_override?: unknown;
}): ExperimentAssignment {
  const override =
    row.content_override &&
    typeof row.content_override === "object" &&
    !Array.isArray(row.content_override)
      ? (row.content_override as ExperimentAssignment["contentOverride"])
      : null;
  return {
    id: row.id,
    experimentId: row.experiment_id,
    status: row.status as AssignmentStatus,
    recommendationReason: getReasonLabel(row.recommendation_reason_code),
    scheduledFor: row.scheduled_for,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    contentOverride: override ?? null,
  };
}

async function fetchExperimentById(id: string): Promise<Experiment | null> {
  const supabase = getSupabase();
  if (!supabase) return getExperimentById(id) ?? null;
  const locale = contentLocale();
  const selectCols =
    "id, slug, category, difficulty, duration_minutes, min_duration_minutes, premium, experiment_content(title, summary, rationale, instructions, smaller_version, reflection_questions)";
  for (const loc of locale === "en" ? [locale, "sv-SE"] : [locale, "en", "sv-SE"]) {
    const { data } = await supabase
      .from("experiments")
      .select(selectCols)
      .eq("id", id)
      .eq("experiment_content.locale", loc)
      .maybeSingle();
    if (data) {
      const mapped = mapJoinedExperiment(
        data as unknown as Parameters<typeof mapJoinedExperiment>[0]
      );
      if (mapped) return mapped;
    }
  }
  return null;
}

export async function listPublishedExperiments(): Promise<Experiment[]> {
  const supabase = getSupabase();
  if (!supabase) return localizeExperiments(MOCK_EXPERIMENTS);
  const client = supabase;

  const locale = contentLocale();
  const selectCols =
    "id, slug, category, difficulty, duration_minutes, min_duration_minutes, premium, experiment_content(title, summary, rationale, instructions, smaller_version, reflection_questions)";

  type ExpRow = Parameters<typeof mapJoinedExperiment>[0];

  async function fetchLocale(loc: string): Promise<ExpRow[] | null> {
    const res = await client
      .from("experiments")
      .select(selectCols)
      .eq("status", "published")
      .eq("experiment_content.locale", loc);
    if (res.error || !res.data) return null;
    return res.data as unknown as ExpRow[];
  }

  const localesToTry =
    locale === "en" ? [locale, "sv-SE"] : [locale, "en", "sv-SE"];

  let rows: ExpRow[] | null = null;
  for (const loc of localesToTry) {
    const fetched = await fetchLocale(loc);
    if (fetched && fetched.some((r) => r.experiment_content?.length)) {
      rows = fetched;
      break;
    }
  }

  if (!rows) return localizeExperiments(MOCK_EXPERIMENTS);

  const mapped = rows
    .map((row) => mapJoinedExperiment(row))
    .filter((e): e is Experiment => e !== null);

  const list = mapped.length > 0 ? mapped : MOCK_EXPERIMENTS;
  // Prefer i18n content when available for the active locale
  return localizeExperiments(list);
}

async function loadRecommendContext(
  excludeIds: string[] = []
): Promise<RecommendContext> {
  const supabase = getSupabase();
  const ctx: RecommendContext = { excludeIds };

  if (!supabase) return ctx;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return ctx;

  const [
    checkIns,
    goalsRes,
    profileRes,
    lifeRes,
    recentAssignments,
    outcomesRes,
  ] = await Promise.all([
    listRecentCheckIns(1),
    supabase
      .from("user_goals")
      .select("goal_code")
      .eq("user_id", user.id)
      .eq("active", true),
    supabase
      .from("profiles")
      .select("available_minutes, joy_pathway, calm_pathway")
      .eq("id", user.id)
      .maybeSingle(),
    supabase
      .from("user_life_situations")
      .select("situation_code")
      .eq("user_id", user.id),
    supabase
      .from("experiment_assignments")
      .select("experiment_id")
      .eq("user_id", user.id)
      .in("status", ["completed", "skipped"])
      .order("created_at", { ascending: false })
      .limit(7),
    supabase
      .from("experiment_outcomes")
      .select(
        "completed, after_mood, after_energy, repeat_intent, experiment_assignments(experiment_id)"
      )
      .eq("user_id", user.id)
      .eq("completed", true)
      .order("created_at", { ascending: false })
      .limit(30),
  ]);

  const last = checkIns[0];
  if (last) {
    ctx.mood = last.mood;
    ctx.energy = last.energy;
    ctx.stress = last.stress;
  }

  ctx.goals = (goalsRes.data ?? []).map((g) => g.goal_code);
  ctx.availableMinutes = profileRes.data?.available_minutes ?? null;
  ctx.joyPathway = profileRes.data?.joy_pathway ?? null;
  ctx.calmPathway = profileRes.data?.calm_pathway ?? null;
  ctx.lifeSituations = (lifeRes.data ?? []).map((r) => r.situation_code);
  ctx.recentExperimentIds = (recentAssignments.data ?? []).map(
    (r) => r.experiment_id
  );

  // Learn from past outcomes: boost what worked, soft-penalize what did not
  // repeat_intent: 0=no, 1=maybe, 2=yes  | after_mood: 0-10
  const catalog = await listPublishedExperiments();
  const byId = new Map(catalog.map((e) => [e.id, e]));
  const positiveExperimentIds: string[] = [];
  const negativeExperimentIds: string[] = [];
  const positiveCategories: string[] = [];
  const categoryHits = new Map<string, number>();

  type OutcomeSignalRow = {
    after_mood: number | null;
    repeat_intent: number | null;
    experiment_assignments:
      | { experiment_id: string }
      | { experiment_id: string }[]
      | null;
  };

  const outcomeRows = (outcomesRes.data ?? []) as unknown as OutcomeSignalRow[];

  for (const row of outcomeRows) {
    const assignment = row.experiment_assignments;
    const a = Array.isArray(assignment) ? assignment[0] : assignment;
    const experimentId = a?.experiment_id;
    if (!experimentId) continue;

    const repeat = row.repeat_intent;
    const afterMood = row.after_mood;
    const positive =
      repeat === 2 || (afterMood != null && afterMood >= 7);
    const negative =
      repeat === 0 || (afterMood != null && afterMood <= 3);

    if (positive) {
      positiveExperimentIds.push(experimentId);
      const exp = byId.get(experimentId);
      if (exp) {
        categoryHits.set(
          exp.category,
          (categoryHits.get(exp.category) ?? 0) + 1
        );
      }
    } else if (negative) {
      negativeExperimentIds.push(experimentId);
    }
  }

  // Top categories by positive outcomes
  const sortedCats = [...categoryHits.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([cat]) => cat);
  positiveCategories.push(...sortedCats.slice(0, 3));

  ctx.positiveExperimentIds = positiveExperimentIds;
  ctx.negativeExperimentIds = negativeExperimentIds;
  ctx.positiveCategories = positiveCategories;
  ctx.allowPremium = await hasEntitlement("premium_experiments");

  // Global weights + similar-user signals (best-effort; recommend works offline without them)
  const [globalStats, similarSignals] = await Promise.all([
    fetchGlobalExperimentStats(),
    fetchSimilarSignals({
      goals: ctx.goals ?? [],
      mood: ctx.mood ?? null,
      stress: ctx.stress ?? null,
      energy: ctx.energy ?? null,
    }),
  ]);
  if (globalStats.length > 0) ctx.globalStats = globalStats;
  if (similarSignals.length > 0) ctx.similarSignals = similarSignals;

  return ctx;
}

async function fetchGlobalExperimentStats(): Promise<ExperimentGlobalStat[]> {
  try {
    const res = await fetch(`${API_BASE}/api/v1/experiments/stats`);
    if (!res.ok) return [];
    const json = (await res.json()) as {
      stats?: Array<{
        experimentId: string;
        nOutcomes: number;
        globalWeight: number;
        completionRate?: number | null;
        avgMoodDelta?: number | null;
        repeatYesRate?: number | null;
      }>;
    };
    return (json.stats ?? []).map((s) => ({
      experimentId: s.experimentId,
      nOutcomes: s.nOutcomes,
      globalWeight: s.globalWeight,
      completionRate: s.completionRate ?? null,
      avgMoodDelta: s.avgMoodDelta ?? null,
      repeatYesRate: s.repeatYesRate ?? null,
    }));
  } catch {
    return [];
  }
}

async function fetchSimilarSignals(input: {
  goals: string[];
  mood?: number | null;
  stress?: number | null;
  energy?: number | null;
}): Promise<SimilarSignal[]> {
  try {
    const token = await getAccessToken();
    if (!token) return [];
    const params = new URLSearchParams();
    if (input.goals.length) params.set("goals", input.goals.join(","));
    if (input.mood != null) params.set("mood", String(input.mood));
    if (input.stress != null) params.set("stress", String(input.stress));
    if (input.energy != null) params.set("energy", String(input.energy));
    const res = await fetch(
      `${API_BASE}/api/v1/experiments/similar-signals?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) return [];
    const json = (await res.json()) as {
      signals?: SimilarSignal[];
    };
    return json.signals ?? [];
  } catch {
    return [];
  }
}

async function insertAssignment(
  experimentId: string,
  reasonCode: string,
  snapshot: {
    source: string;
    score?: number;
    goals?: string[];
  }
): Promise<ExperimentAssignment | null> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      id: crypto.randomUUID(),
      experimentId,
      status: "assigned",
      recommendationReason: getReasonLabel(reasonCode),
      scheduledFor: new Date().toISOString(),
      startedAt: null,
      completedAt: null,
    };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: created, error } = await supabase
    .from("experiment_assignments")
    .insert({
      user_id: user.id,
      experiment_id: experimentId,
      recommendation_reason_code: reasonCode,
      recommendation_snapshot: snapshot,
      status: "assigned",
      scheduled_for: new Date().toISOString(),
    })
    .select("*")
    .single();

  if (error || !created) return null;
  return mapAssignment(created);
}

export async function ensureTodayAssignment(): Promise<{
  assignment: ExperimentAssignment | null;
  experiment: Experiment | null;
}> {
  const supabase = getSupabase();
  if (!supabase) {
    const catalog = localizeExperiments(MOCK_EXPERIMENTS);
    const rec = recommendExperiment(catalog, {});
    const exp = rec?.experiment ?? catalog[0] ?? null;
    if (!exp) return { assignment: null, experiment: null };
    return {
      assignment: {
        id: "22222222-2222-4222-8222-222222222201",
        experimentId: exp.id,
        status: "assigned",
        recommendationReason:
          rec?.reasonLabel ?? getReasonLabel("default"),
        scheduledFor: new Date().toISOString(),
        startedAt: null,
        completedAt: null,
      },
      experiment: exp,
    };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { assignment: null, experiment: null };

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);

  const catalog = await listPublishedExperiments();

  // Prefer open clinician homework (ordered sequence) over daily algorithm
  const { data: clinicianRows } = await supabase
    .from("experiment_assignments")
    .select("*")
    .eq("user_id", user.id)
    .eq("source", "clinician")
    .in("status", ["assigned", "started"])
    .order("created_at", { ascending: true })
    .limit(20);

  const clinicianHw = [...(clinicianRows ?? [])].sort((a, b) => {
    const sa =
      (a.recommendation_snapshot as { sequenceIndex?: number } | null)
        ?.sequenceIndex ?? 999;
    const sb =
      (b.recommendation_snapshot as { sequenceIndex?: number } | null)
        ?.sequenceIndex ?? 999;
    if (sa !== sb) return sa - sb;
    const da = a.due_at ? new Date(a.due_at as string).getTime() : 0;
    const db = b.due_at ? new Date(b.due_at as string).getTime() : 0;
    return da - db;
  })[0];

  if (clinicianHw) {
    let experiment =
      catalog.find((e) => e.id === clinicianHw.experiment_id) ?? null;
    if (!experiment) {
      experiment = await fetchExperimentById(clinicianHw.experiment_id as string);
    }
    const mapped = mapAssignment(clinicianHw);
    if (experiment && mapped.contentOverride) {
      const o = mapped.contentOverride;
      experiment = {
        ...experiment,
        ...(o.title != null ? { title: o.title } : {}),
        ...(o.summary != null ? { summary: o.summary } : {}),
        ...(o.rationale != null ? { rationale: o.rationale } : {}),
        ...(o.instructions != null ? { instructions: o.instructions } : {}),
        ...(o.smallerVersion != null
          ? { smallerVersion: o.smallerVersion }
          : {}),
        ...(o.reflectionQuestions != null
          ? { reflectionQuestions: o.reflectionQuestions }
          : {}),
        ...(o.durationMinutes != null
          ? { durationMinutes: o.durationMinutes }
          : {}),
        ...(o.difficulty != null ? { difficulty: o.difficulty } : {}),
        ...(o.category != null ? { category: o.category } : {}),
      };
    }
    return {
      assignment: {
        ...mapped,
        recommendationReason:
          clinicianHw.clinician_note_shared ||
          mapped.recommendationReason ||
          "Från din terapeut",
      },
      experiment,
    };
  }

  const { data: existing } = await supabase
    .from("experiment_assignments")
    .select("*")
    .eq("user_id", user.id)
    .in("status", ["assigned", "started", "completed"])
    .gte("created_at", startOfDay.toISOString())
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (existing) {
    const experiment =
      catalog.find((e) => e.id === existing.experiment_id) ?? null;
    return { assignment: mapAssignment(existing), experiment };
  }

  if (catalog.length === 0) return { assignment: null, experiment: null };

  const ctx = await loadRecommendContext();
  const rec = recommendExperiment(catalog, ctx);
  if (!rec) return { assignment: null, experiment: null };

  const assignment = await insertAssignment(rec.experiment.id, rec.reasonCode, {
    source: "client_recommend_v2",
    score: rec.score,
    goals: ctx.goals ?? [],
  });

  return {
    assignment,
    experiment: rec.experiment,
  };
}

export type AssignExperimentOptions = {
  /** Override recommendation reason code (default: pick) */
  reasonCode?: string;
  /** Human-readable reason shown in UI */
  reasonLabel?: string;
  /** Snapshot source tag for analytics */
  source?: string;
};

/**
 * Assign a specific experiment (from Explore or Programs).
 * Replaces open today assignment if any.
 */
export async function assignExperiment(
  experimentId: string,
  options?: AssignExperimentOptions
): Promise<{
  assignment: ExperimentAssignment | null;
  experiment: Experiment | null;
}> {
  const catalog = await listPublishedExperiments();
  const experiment = catalog.find((e) => e.id === experimentId) ?? null;
  if (!experiment) return { assignment: null, experiment: null };

  const supabase = getSupabase();
  if (supabase) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      // Do not skip clinician homework when picking another experiment
      await supabase
        .from("experiment_assignments")
        .update({
          status: "skipped",
          skipped_at: new Date().toISOString(),
        })
        .eq("user_id", user.id)
        .in("status", ["assigned", "started"])
        .neq("source", "clinician")
        .gte("created_at", startOfDay.toISOString());
    }
  }

  const reasonCode = options?.reasonCode ?? "pick";
  const assignment = await insertAssignment(experimentId, reasonCode, {
    source: options?.source ?? "user_pick",
    ...(options?.reasonLabel ? { goals: [options.reasonLabel] } : {}),
  });

  // Prefer explicit reasonLabel on client-side assignment when provided
  if (assignment && options?.reasonLabel) {
    return {
      assignment: { ...assignment, recommendationReason: options.reasonLabel },
      experiment,
    };
  }

  return { assignment, experiment };
}

/**
 * Skip current open assignment and recommend a different one.
 */
export async function swapTodayAssignment(
  currentAssignmentId?: string | null
): Promise<{
  assignment: ExperimentAssignment | null;
  experiment: Experiment | null;
}> {
  const supabase = getSupabase();
  const catalog = await listPublishedExperiments();

  let excludeIds: string[] = [];

  if (supabase) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      if (currentAssignmentId) {
        // Never auto-skip clinician homework on swap
        const { data: cur } = await supabase
          .from("experiment_assignments")
          .select("source")
          .eq("id", currentAssignmentId)
          .eq("user_id", user.id)
          .maybeSingle();
        if (cur?.source !== "clinician") {
          await supabase
            .from("experiment_assignments")
            .update({
              status: "skipped",
              skipped_at: new Date().toISOString(),
            })
            .eq("id", currentAssignmentId)
            .eq("user_id", user.id);
        }
      } else {
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        await supabase
          .from("experiment_assignments")
          .update({
            status: "skipped",
            skipped_at: new Date().toISOString(),
          })
          .eq("user_id", user.id)
          .in("status", ["assigned", "started"])
          .neq("source", "clinician")
          .gte("created_at", startOfDay.toISOString());
      }

      const { data: open } = await supabase
        .from("experiment_assignments")
        .select("experiment_id")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(5);
      excludeIds = (open ?? []).map((r) => r.experiment_id);
    }
  } else if (currentAssignmentId) {
    // demo: exclude current catalog pick
    const demo = catalog[0];
    if (demo) excludeIds = [demo.id];
  }

  const ctx = await loadRecommendContext(excludeIds);
  const rec = recommendExperiment(catalog, ctx);
  if (!rec) return { assignment: null, experiment: null };

  const assignment = await insertAssignment(rec.experiment.id, rec.reasonCode, {
    source: "client_swap_v1",
    score: rec.score,
  });
  return { assignment, experiment: rec.experiment };
}

export async function skipAssignment(assignmentId: string): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return true;
  const now = new Date().toISOString();
  const { error } = await supabase
    .from("experiment_assignments")
    .update({ status: "skipped", skipped_at: now })
    .eq("id", assignmentId);
  return !error;
}

export async function startAssignment(assignmentId: string): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) {
    enqueue({ type: "START_EXPERIMENT", payload: { assignmentId } });
    return true;
  }
  const now = new Date().toISOString();
  const { error } = await supabase
    .from("experiment_assignments")
    .update({ status: "started", started_at: now })
    .eq("id", assignmentId);
  if (error) {
    enqueue({ type: "START_EXPERIMENT", payload: { assignmentId } });
    return false;
  }
  return true;
}

export async function completeAssignment(
  assignmentId: string,
  outcome: Omit<ExperimentOutcome, "assignmentId">
): Promise<boolean> {
  const supabase = getSupabase();
  const payload = { assignmentId, ...outcome };

  if (!supabase) {
    enqueue({ type: "COMPLETE_EXPERIMENT", payload });
    return true;
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    enqueue({ type: "COMPLETE_EXPERIMENT", payload });
    return false;
  }

  const now = new Date().toISOString();
  const { error: aErr } = await supabase
    .from("experiment_assignments")
    .update({ status: "completed", completed_at: now })
    .eq("id", assignmentId);

  const { error: oErr } = await supabase.from("experiment_outcomes").insert({
    assignment_id: assignmentId,
    user_id: user.id,
    completed: outcome.completed,
    after_mood: outcome.afterMood,
    after_energy: outcome.afterEnergy,
    repeat_intent: outcome.repeatIntent,
    reflection_ciphertext: outcome.reflection ?? null,
  });

  if (aErr || oErr) {
    enqueue({ type: "COMPLETE_EXPERIMENT", payload });
    return false;
  }
  return true;
}

export type OutcomeWithMeta = {
  assignmentId: string;
  experimentId: string | null;
  completed: boolean;
  afterMood: number | null;
  afterEnergy: number | null;
  repeatIntent: number | null;
  reflection: string | null;
  completedAt: string | null;
  category?: string | undefined;
  title?: string | undefined;
};

type OutcomeListRow = {
  assignment_id: string;
  completed: boolean;
  after_mood: number | null;
  after_energy: number | null;
  repeat_intent: number | null;
  reflection_ciphertext: string | null;
  created_at: string;
  experiment_assignments:
    | { experiment_id: string; completed_at: string | null }
    | { experiment_id: string; completed_at: string | null }[]
    | null;
};

export async function listOutcomes(): Promise<OutcomeWithMeta[]> {
  const supabase = getSupabase();
  if (!supabase) return [];
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  const { data } = await supabase
    .from("experiment_outcomes")
    .select(
      "assignment_id, completed, after_mood, after_energy, repeat_intent, reflection_ciphertext, created_at, experiment_assignments(experiment_id, completed_at)"
    )
    .eq("user_id", user.id)
    .order("created_at", { ascending: false })
    .limit(50);

  if (!data) return [];

  const catalog = await listPublishedExperiments();
  const byId = new Map(catalog.map((e) => [e.id, e]));
  const rows = data as unknown as OutcomeListRow[];

  return rows.map((row) => {
    const assignment = row.experiment_assignments;
    const a = Array.isArray(assignment) ? assignment[0] : assignment;
    const experimentId = a?.experiment_id ?? null;
    const exp = experimentId ? byId.get(experimentId) : undefined;
    const result: OutcomeWithMeta = {
      assignmentId: row.assignment_id,
      experimentId,
      completed: row.completed,
      afterMood: row.after_mood,
      afterEnergy: row.after_energy,
      repeatIntent: row.repeat_intent,
      reflection: row.reflection_ciphertext,
      completedAt: a?.completed_at ?? row.created_at,
    };
    if (exp?.category) result.category = exp.category;
    if (exp?.title) result.title = exp.title;
    return result;
  });
}
